import { useEffect, useState } from 'react';
import type { tLeadAgentIndicatorStage } from './useLeadAgentIndicator.hook';

// hidden → waiting → entering → shown → leaving → hidden
type tPresence = 'hidden' | 'waiting' | 'entering' | 'shown' | 'leaving';

// Motion values come from the DS tokens, never literals (guidelines/motion.md,
// M1). A token that did not load reads as 0: the indicator then shows and
// leaves at once instead of hanging.
function tokenMs(token: string): number {
  const [duration = ''] = getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim()
    .split(/\s+/);
  const value = parseFloat(duration);
  if (Number.isNaN(value)) return 0;
  return duration.endsWith('ms') ? value : value * 1000;
}

function after(token: string, action: () => void) {
  const timer = setTimeout(action, tokenMs(token));
  return () => clearTimeout(timer);
}

/**
 * When the indicator is on screen (guidelines/motion.md, Waiting and loading):
 * it appears once the wait passes about 400 ms, so a fast reply never flashes
 * it; once it starts entering it finishes, so it stays on screen at least its
 * enter plus its exit (about 500 ms); and it stays mounted while it leaves.
 */
export default function useIndicatorPresence(stage: tLeadAgentIndicatorStage | null) {
  const [presence, setPresence] = useState<tPresence>('hidden');
  // What the pill says while it is on screen, the last stage included while it
  // leaves.
  const [shownStage, setShownStage] = useState(stage);

  useEffect(() => {
    if (stage) setShownStage(stage);
  }, [stage]);

  // What a new or finished wait changes at once.
  useEffect(() => {
    if (stage && presence === 'hidden') setPresence('waiting');
    if (stage && presence === 'leaving') setPresence('entering');
    if (!stage && presence === 'waiting') setPresence('hidden');
    if (!stage && presence === 'shown') setPresence('leaving');
  }, [stage, presence]);

  // What takes the time of a token. Keyed on the presence alone, so a stage
  // change never restarts it.
  useEffect(() => {
    if (presence === 'waiting')
      return after('--ls-duration-xl', () => setPresence('entering'));
    if (presence === 'entering')
      return after('--ls-motion-notify-enter', () => setPresence('shown'));
    if (presence === 'leaving')
      return after('--ls-motion-notify-exit', () => setPresence('hidden'));
    return undefined;
  }, [presence]);

  const open = presence === 'entering' || presence === 'shown';
  const leaving = presence === 'leaving';
  return {
    open,
    leaving,
    stage: open || leaving ? (stage ?? shownStage) : null,
  };
}
